import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Col,
  Divider,
  Row,
  Form,
  Switch,
  Button,
  Table,
  Checkbox,
  message,
  Skeleton
} from 'antd';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useCurrentWorkspace } from '@hooks/workspace';

import {
  getNotificationPreferences,
  updateNotificationPreferences
} from '@api/Profile';
import { DisabledPreferencesDto, PreferencesItemDto } from '@api/Api';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import { ReactComponent as SelectSvg } from '@assets/icons/select-menu-item.svg';
import { useAuth } from '@hooks/useAuth';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { useCampaignsQuery } from '@hooks/queries/campaigns';

function getValue(obg: object): string[] {
  if (!obg || typeof obg !== 'object') {
    return [];
  }
  return Object.entries(obg)
    .filter(([_, value]) => value === false)
    .map(([key]) => key);
}

let successTime: ReturnType<typeof setTimeout>;

type TItemData = {
  key: PreferencesItemDto['email'][number];
  name: string;
  disabledToTurnOff?: boolean;
};

export default function PerWorkspaceSettings() {
  const [currentWorkspace] = useCurrentWorkspace();
  const { currentOrganization } = useOrganization();
  const dispatch = useDispatch();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const canUpgradeAwNotificationSettings =
    currentOrganization.entity?.features.awNotificationSettings;

  const data: TItemData[] = useMemo(
    () => [
      {
        name: 'Someone invites me to a project',
        key: 'you-added-to-campaign'
      },
      {
        name: 'Someone invites me to a private folder',
        key: 'you-added-to-folder'
      },
      {
        name: 'Someone assigns me to a task',
        key: 'you-assigned-to-task'
      },
      {
        name: 'Someone mentions me in a task',
        key: 'mention-in-task'
      },
      {
        name: 'Someone comments or replies to a task I created',
        key: 'new-comment-in-owned-task'
      },
      {
        name: 'Someone comments or replies to a task I am assigned to',
        key: 'new-comment-in-assigned-task'
      },
      {
        name: 'Task status changes',
        key: 'task-status-changed'
      },
      {
        name: 'Task due date changes',
        key: 'task-due-date-changed'
      },
      {
        name: 'A new media has been uploaded',
        key: 'new-asset'
      },
      {
        name: 'A new version of a media has been uploaded',
        key: 'new-asset-version'
      },
      {
        name: 'Someone new has joined a project I have access to',
        key: 'joined-to-campaign'
      },
      {
        name: 'Someone new has joined a folder I have access to',
        key: 'joined-to-folder'
      },
      {
        name: 'Someone comments on a media I have access to',
        key: 'new-comment-in-asset'
      },
      {
        name: 'Someone replies to my comment on a media',
        key: 'reply-to-comment-in-asset'
      },
      {
        name: 'Someone mentions me in a comment on a media',
        key: 'mention-in-comment-in-asset'
      },
      {
        name: 'Someone invites me to review a media',
        key: 'aw-invitation'
      },
      {
        name: 'Someone granted me permission to manage approval routing',
        key: 'aw-created',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      },
      {
        name: 'A new stage started on approval routing I can manage',
        key: 'aw-new-stage-started',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      },
      {
        name: 'Approval routing has been automatically paused or restarted',
        key: 'aw-new-asset-version',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      },
      {
        name: 'Approval routing due date has changed',
        key: 'aw-stage-due-date-changed',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      },
      {
        name: 'A reviewer has submitted a decision on a media',
        key: 'aw-decision-submitted',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      },
      {
        name: 'Final approval decision determined for a media',
        key: 'aw-final-decision-submitted',
        disabledToTurnOff: !canUpgradeAwNotificationSettings
      }
    ],
    [canUpgradeAwNotificationSettings]
  );

  const [notificationDisabledData, setNotificationDisabledData] =
    useState<PreferencesItemDto>({
      email: [],
      inApp: [],
      chat: [],
      campaigns: []
    });
  const [form] = Form.useForm();
  const emailValues = Form.useWatch('email', form);
  const inAppValues = Form.useWatch('inApp', form);
  const chatValues = Form.useWatch('chat', form);

  const getCheckedValues = useCallback(
    (values: Record<string, boolean>, fieldPrefix: string) =>
      Object.entries(values)
        .filter(([key]) => {
          const fieldInstance = form.getFieldInstance([fieldPrefix, key]);
          return fieldInstance && !fieldInstance.input?.disabled;
        })
        .every(([, value]) => value === false),
    [form]
  );

  const isAllDisabled = useMemo(() => {
    return (
      emailValues &&
      getCheckedValues(emailValues, 'email') &&
      inAppValues &&
      getCheckedValues(inAppValues, 'inApp') &&
      chatValues &&
      getCheckedValues(chatValues, 'chat')
    );
  }, [emailValues, inAppValues, chatValues, getCheckedValues]);

  const { user } = useAuth(true, 'internal');
  const anyChatConnected = user.slackConnected;

  const campaignsQuery = useCampaignsQuery({
    workspaceId: currentWorkspace.id
  });
  const campaignsList = useMemo(() => {
    return campaignsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [campaignsQuery.data]);

  useEffect(() => {
    (async () => {
      const {
        data: { disabled }
      }: { data: { disabled: PreferencesItemDto } } =
        await getNotificationPreferences({
          workspaceId: currentWorkspace.id
        });
      setNotificationDisabledData(disabled);
    })();
  }, [currentWorkspace.id]);

  useEffect(() => {
    const { email, inApp, chat, campaigns } = notificationDisabledData;
    const defaultValues: {
      name: string | string[];
      value: boolean;
    }[] = [];
    data.forEach((element: TItemData) => {
      defaultValues.push({
        name: ['email', element.key],
        value: !email.includes(element.key)
      });
      defaultValues.push({
        name: ['inApp', element.key],
        value: !inApp.includes(element.key)
      });
      defaultValues.push({
        name: ['chat', element.key],
        value: !chat.includes(element.key)
      });
    });
    campaigns.forEach((item: string) => {
      defaultValues.push({
        name: ['campaigns', item],
        value: false
      });
    });
    form.setFields(defaultValues);
  }, [notificationDisabledData]);

  const onSubmit = async (data: any) => {
    try {
      setIsSuccess(false);
      setIsSubmit(true);
      clearInterval(successTime);
      const email = getValue(data.email);
      const inApp = getValue(data.inApp);
      const chat = getValue(data.chat);
      const campaigns = getValue(data.campaigns);
      await updateNotificationPreferences({
        workspaceId: currentWorkspace.id,
        disabled: {
          email,
          inApp,
          chat,
          campaigns
        } as DisabledPreferencesDto
      });
      setIsSuccess(true);
      successTime = setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.warn(error, '-> updateNotificationPreferences');
      message.error('Something went wrong');
    } finally {
      setIsSubmit(false);
    }
  };

  const setAll = (enabled: boolean) => {
    const defaultValues: any = [];
    data.forEach((element: TItemData) => {
      const { key, disabledToTurnOff } = element;
      const value = disabledToTurnOff ? true : enabled;
      defaultValues.push({
        name: ['email', key],
        value
      });
      defaultValues.push({
        name: ['inApp', key],
        value
      });
      defaultValues.push({
        name: ['chat', key],
        value: !anyChatConnected ? true : value
      });
    });
    form.setFields(defaultValues);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Notifications',
        dataIndex: 'name',
        render: (text: string, node: TItemData) => {
          const isCanVisible = !!node.disabledToTurnOff;
          return (
            <UpgradePlanTooltip
              text=" to a Team plan to access this feature."
              placement="topLeft"
              isCanVisible={isCanVisible}
            >
              <div className="notifications_settings-row">
                <div className="notifications_settings-row-name">
                  <span style={isCanVisible ? { opacity: 0.25 } : {}}>
                    {text}
                  </span>
                  {isCanVisible && <UpgradePlanSvg />}
                </div>
              </div>
            </UpgradePlanTooltip>
          );
        }
      },
      {
        title: 'Email',
        dataIndex: 'email',
        className: 'b-email-notification',
        render: (text: string, node: TItemData) => {
          const disabledToTurnOff =
            emailValues?.[node.key] && node.disabledToTurnOff;
          return (
            <Form.Item name={['email', node.key]} valuePropName="checked">
              <Checkbox
                className="b-checkbox-blue"
                disabled={disabledToTurnOff}
              />
            </Form.Item>
          );
        }
      },
      {
        title: 'Web (in-app)',
        dataIndex: 'inApp',
        className: 'b-web-app-notification',
        render: (text: string, node: TItemData) => {
          const disabledToTurnOff =
            inAppValues?.[node.key] && node.disabledToTurnOff;
          return (
            <Form.Item name={['inApp', node.key]} valuePropName="checked">
              <Checkbox
                className="b-checkbox-blue"
                disabled={disabledToTurnOff}
              />
            </Form.Item>
          );
        }
      },
      {
        title: 'Chat app',
        dataIndex: 'chat',
        className: 'b-web-app-notification',
        render: (text: string, node: TItemData) => {
          const disabledToTurnOff =
            chatValues?.[node.key] && node.disabledToTurnOff;
          return (
            <Form.Item name={['chat', node.key]} valuePropName="checked">
              <Checkbox
                className="b-checkbox-blue"
                disabled={!anyChatConnected || disabledToTurnOff}
              />
            </Form.Item>
          );
        }
      }
    ],
    [anyChatConnected, emailValues, inAppValues, chatValues]
  );

  return (
    <Form
      form={form}
      className="notifications_settings_form"
      onFinish={onSubmit}
    >
      <div className="notifications_settings">
        {campaignsList.length !== 0 ? (
          <>
            <div className="profile_settings_subtitle">
              Project subscriptions
            </div>
            <div className="profile_settings_text b-gray-color">
              Choose which projects you would like to receive notifications
              from.
            </div>
            <div
              className="notifications_campaigns"
              style={{
                overflowX: 'hidden'
              }}
            >
              <InfiniteScroll
                loading={campaignsQuery.isFetchingNextPage ? 'more' : false}
                next={campaignsQuery.fetchNextPage}
                hasMore={campaignsQuery.hasNextPage || false}
                loader={
                  <Row
                    className="notifications_campaigns-block"
                    align="middle"
                    wrap={false}
                    gutter={16}
                  >
                    <Col className="notifications_campaigns-img">
                      <Skeleton.Input
                        style={{
                          width: '56px',
                          minWidth: '1px'
                        }}
                      />
                    </Col>
                    <Col className="notifications_campaigns-name">
                      <Skeleton.Input />
                    </Col>
                    <Col className="notifications_campaigns-switch">
                      <Skeleton.Input />
                    </Col>
                  </Row>
                }
              >
                {campaignsList.map(({ node }) => {
                  const asset = node?.topAssets?.[0];
                  const image = assetPreviewThumbnails({
                    url: asset?.previewUrl || '',
                    status: asset?.status || '',
                    type: asset?.type || ''
                  });
                  return (
                    <Row
                      className="notifications_campaigns-block"
                      align="middle"
                      wrap={false}
                      gutter={16}
                      key={node.id}
                    >
                      <Col className="notifications_campaigns-img">
                        {typeof image === 'string' ? (
                          <img src={image} alt="" />
                        ) : (
                          image
                        )}
                      </Col>
                      <Col className="notifications_campaigns-name">
                        {node.name}
                      </Col>
                      <Col className="notifications_campaigns-switch">
                        <Form.Item
                          className="profile_settings_switch"
                          name={['campaigns', node.id]}
                          valuePropName="checked"
                        >
                          <Switch
                            checkedChildren="On"
                            unCheckedChildren="Off"
                            defaultChecked={true}
                            className="profile_settings_switch-control"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                })}
              </InfiniteScroll>
            </div>
            <Divider className="profile_settings_divider" />
          </>
        ) : null}
        <Row
          className="notifications_block"
          wrap={false}
          align="bottom"
          justify="space-between"
          gutter={16}
        >
          <Col>
            <div className="profile_settings_subtitle">Notify me when:</div>
            <div className="profile_settings_text b-gray-color">
              Choose which notifications you would like to receive for the
              projects you are subscribed to in StreamWork.
            </div>
          </Col>
          <Col>
            <Button
              type="text"
              htmlType="button"
              onClick={() => setAll(isAllDisabled)}
              key={isAllDisabled}
            >
              {isAllDisabled ? 'Enable all' : 'Disable all'}
            </Button>
          </Col>
        </Row>
        <div className="table_container">
          <Table columns={columns} pagination={false} dataSource={data} />
        </div>
      </div>
      <Button
        className="notifications_settings_form-btn"
        htmlType="submit"
        type="primary"
        loading={isSubmit}
        icon={isSuccess && <SelectSvg className="b-icon-select" />}
      >
        {isSuccess ? 'Saved' : 'Save'}
      </Button>
    </Form>
  );
}
